import { useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCanonicalSpaPathname,
  normalizePathname,
} from '../routes/knownRoutePatterns';
import { ROUTE_PATHS } from '../routes/routePaths';

function isOnNotFoundPath(pathname: string): boolean {
  return pathname === ROUTE_PATHS.NOT_FOUND_PAGE;
}

/**
 * - Legacy `.cfm` → handled by `UnknownPathRoute` on the splat route (declarative redirect; avoids NotFound flash).
 * - Unknown non-.cfm → left alone entirely, so the splat route's `UnknownPathRoute` renders the
 *   not-found UI in place at whatever URL was actually hit (matches aosphere-nextgenui-user's
 *   behavior — the address bar and the error log's pageUrl both show the real missing URL,
 *   not a generic /not-found it got rewritten to).
 * - Known route under a URL prefix → `replace` to the canonical pathname React Router registers.
 */
export default function AppLocationGuard() {
  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const { pathname, search, hash } = location;

    if (isOnNotFoundPath(pathname)) {
      return;
    }

    const canonical = getCanonicalSpaPathname(pathname);
    if (canonical === null) {
      return;
    }
    const normalizedPath = normalizePathname(pathname);
    if (canonical !== normalizedPath) {
      void navigate(
        { pathname: canonical, search, hash },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}
